/**
 * Public Survey Wrapper
 * 
 * Routes to the appropriate survey component based on event version.
 * V1 events use the new V1 survey pages in public_event/v1/pages.
 * V2 events use the existing PublicSurvey component.
 */

import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useEventContext } from '../../contexts/EventContext';
import { useEventDetails } from '../../../hooks/useEventDetails';
import { PublicSurvey } from './PublicSurvey';
import { EventSurveyPageV1 } from '../../v1/pages/EventSurveyPageV1';
import { SessionSurveyPageV1 } from '../../v1/pages/SessionSurveyPageV1';

export const PublicSurveyWrapper: React.FC = () => {
  const { surveySlug } = useParams<{ surveySlug: string }>();
  const location = useLocation();
  const { eventSlug } = useEventContext();
  const { data: eventDetails, isLoading } = useEventDetails(eventSlug);
  
  // Debug logging
  console.log('[PublicSurveyWrapper] Event details:', {
    eventDetails,
    isV1Event: eventDetails?.isV1Event,
    eventSlug,
    surveySlug,
    pathname: location.pathname,
    isLoading
  });
  
  // Wait for event details to load
  if (isLoading || !eventDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading survey...</div>
      </div>
    );
  }
  
  // Check if this is a V1 event
  const isV1Event = eventDetails.isV1Event === true;
  
  // For V1 events, determine if it's a session or event survey
  if (isV1Event) {
    // Check if we're in a session survey path
    // Pattern: /:eventSlug/days/:daySlug/sessions/:sessionSlug/surveys/:surveySlug
    const pathParts = location.pathname.split('/');
    const isSessionSurvey = pathParts.includes('days') && pathParts.includes('sessions');
    
    console.log('[PublicSurveyWrapper] V1 survey detected:', {
      isSessionSurvey,
      pathParts
    });
    
    if (isSessionSurvey) {
      return <SessionSurveyPageV1 />;
    } else {
      return <EventSurveyPageV1 />;
    }
  }
  
  // V2 events use the existing PublicSurvey component
  console.log('[PublicSurveyWrapper] Using V2 PublicSurvey component');
  return <PublicSurvey />;
};